// ClientFormModal.jsx
// CREATE: full 5-step wizard (personal + optional first trip).
// EDIT:   personal details only — it NO LONGER sends the trips array, so editing a
//         client can never wipe their other trips. Trips are managed in the detail modal.
import React, { useState } from "react";
import { Btn } from "../ui";
import { Ico } from "./primitives";
import { StepIndicator, Step1, Step2, Step3, Step4, Step5 } from "./formSteps";
import { CLIENT_STEPS, normaliseDestinations, tripToFormData } from "./constants";
import { BRAND } from "../brand";

// onOpenExisting(client): called when a matching existing client is picked while creating.
export default function ClientFormModal({ initial = null, onSubmit, onClose, saving, onOpenExisting }) {
  const isEdit = !!initial;
  const steps = isEdit ? CLIENT_STEPS.slice(0, 1) : CLIENT_STEPS; // edit = personal only
  const [step, setStep] = useState(1);

  const trip = initial?.trips?.[0] || {};
  const [data, setData] = useState({
    fullName: initial?.fullName || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    nationality: initial?.nationality || "",
    countryOfResidence: initial?.countryOfResidence || "",
    returningClient: initial?.returningClient || false,
    tripData: isEdit
      ? tripToFormData(trip)
      : { payment: { currency: BRAND.currency }, refund: { refundRequested: false } },
  });

  const ok = step === 1 ? data.fullName.trim() && data.email.trim() : true;

  const handleSubmit = () => {
    // refundDraft is created separately (as a real refund) once the client exists.
    const { tripData, refundDraft, ...clientFields } = data;

    if (isEdit) {
      // Personal fields only — never touch trips here.
      onSubmit(clientFields);
      return;
    }

    // Create: bundle the first trip in (if anything was entered).
    const normalisedTripData = normaliseDestinations(tripData);
    const tripPayload =
      normalisedTripData && Object.keys(normalisedTripData).length > 0
        ? [normalisedTripData]
        : [];
    onSubmit(
      {
        ...clientFields,
        ...(tripPayload.length ? { trips: tripPayload } : {}),
      },
      refundDraft || null,
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(12px)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col"
        style={{ animation: "modalIn .22s cubic-bezier(.34,1.56,.64,1)" }}
      >
        <div className="px-7 pt-6 pb-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-black text-slate-800">{isEdit ? "Edit Client" : "New Client"}</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {isEdit
                  ? "Personal details — manage trips from the client's detail view"
                  : `${steps[step - 1].label} · Step ${step} of ${steps.length}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-400 flex items-center justify-center text-slate-400 transition-all"
            >
              <Ico d="M6 18L18 6M6 6l12 12" sz={13} />
            </button>
          </div>
          {steps.length > 1 && <StepIndicator steps={steps} current={step} onStepClick={setStep} />}
        </div>

        <div className="flex-1 overflow-y-auto px-7 py-5">
          {step === 1 && (
            <Step1 data={data} onChange={setData} suggestNames={!isEdit} onOpenExisting={onOpenExisting} />
          )}
          {step === 2 && <Step2 data={data} onChange={setData} />}
          {step === 3 && <Step3 data={data} onChange={setData} />}
          {step === 4 && <Step4 data={data} onChange={setData} />}
          {step === 5 && <Step5 data={data} onChange={setData} />}
        </div>

        <div className="px-7 py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
          <Btn variant="secondary" size="sm" disabled={step === 1} onClick={() => setStep((s) => s - 1)}>
            ← Back
          </Btn>
          {step < steps.length ? (
            <Btn variant="primary" disabled={!ok} onClick={() => setStep((s) => s + 1)}>
              Continue →
            </Btn>
          ) : (
            <Btn variant="primary" loading={saving} onClick={handleSubmit}>
              {isEdit ? "Save Changes" : "Create Client"}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}